import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import { DOCUMENT, documentEyebrowSx, documentMapBoxSx, documentNavLogoSx, getDocumentInitials, pageShellSx } from '../constants/documentTheme';
import { formatMoney, getGalleryUrls, getPackageCoverSrc, isPackageExpired } from '../utils/partnerListUtils';
import {
  addOnAmount,
  clampAddOnQuantity,
  isIncludedPackageItem,
  MAX_PACKAGE_ADDON_QUANTITY,
  packageAddOnItems,
  selectedAddOnTotal as sumSelectedAddOns,
} from '../utils/packageItems';
import { headerBgFromClientBranding } from '../utils/partnerWorkspaceBrand';
import MapLink from './MapLink';
import PartnerLockup from './PartnerLockup';
import { SellerDisclosureBox, sellerFromPackage } from './SellerDisclosures';

const wrapSx = {
  ...pageShellSx,
};

// The site navbar is hidden on /packages/:slug, and overlay previews scroll
// inside DocumentPageOverlay. Pin the book bar to the scrollport top.
const PACKAGE_BOOK_BAR_TOP = 0;
// Desktop booking card sits just under that bar.
const PACKAGE_ASIDE_TOP = { md: 72 };

const sectionTitleSx = {
  fontFamily: DOCUMENT.display,
  fontSize: 26,
  fontWeight: 400,
  pb: 1.5,
  mb: 2.5,
  borderBottom: `1px solid ${DOCUMENT.ink}`,
  color: DOCUMENT.ink,
};

const goldCtaSx = {
  background: DOCUMENT.navAccent,
  color: DOCUMENT.nav,
  border: 0,
  borderRadius: 0,
  font: 'inherit',
  fontSize: 11,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  px: 2.75,
  py: 1.5,
  minWidth: 0,
  boxShadow: 'none',
  '&:hover': {
    background: `color-mix(in srgb, ${DOCUMENT.navAccent} 72%, white)`,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    background: DOCUMENT.ruleHard,
    color: DOCUMENT.ink3,
  },
};

const goldOutlineCtaSx = {
  ...goldCtaSx,
  background: 'transparent',
  color: DOCUMENT.navAccent,
  border: `1px solid ${DOCUMENT.navAccent}`,
  '&:hover': {
    background: 'rgba(201,169,97,0.14)',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    background: 'transparent',
    color: 'rgba(201,169,97,0.45)',
    borderColor: 'rgba(201,169,97,0.35)',
  },
};

const sidebarClientCtaSx = {
  width: '100%',
  mt: 1.25,
  background: 'none',
  color: DOCUMENT.oxblood,
  border: `1px solid ${DOCUMENT.oxblood}`,
  borderRadius: 0,
  font: 'inherit',
  fontSize: 11,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  py: 1.5,
  boxShadow: 'none',
  '&:hover': {
    background: 'rgba(140,43,34,0.06)',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: DOCUMENT.ink3,
    borderColor: DOCUMENT.ruleHard,
  },
};

const oxbloodCtaSx = {
  width: '100%',
  background: DOCUMENT.oxblood,
  color: DOCUMENT.ivory,
  border: 0,
  borderRadius: 0,
  font: 'inherit',
  fontSize: 11,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  py: 1.75,
  boxShadow: 'none',
  '&:hover': {
    background: DOCUMENT.nav,
    boxShadow: 'none',
  },
};

const splitList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value)
    .split(/\n|;|•|\u2022/)
    .map((item) => item.replace(/^[-–—]\s*/, '').trim())
    .filter(Boolean);
};

const formatClock = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatRange = (from, until, fallback) => {
  if (!from && !until) return fallback;
  const start = from
    ? new Date(from).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : fallback;
  const end = until
    ? new Date(until).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : fallback;
  return `${start} – ${end}`;
};

const categoryKey = (category) => {
  const map = {
    flight: 'catFlight',
    hotel: 'catCheckIn',
    accommodation: 'catCheckIn',
    restaurant: 'catMeal',
    activity: 'catExcursion',
    transport: 'catTransport',
    car_rental: 'catTransport',
    event: 'catActivity',
    free_time: 'catOpen',
    other: 'catActivity',
  };
  return map[category] || 'catActivity';
};

const PackagePhoto = ({ src, alt, ratio = '4 / 3' }) => {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: ratio,
        bgcolor: DOCUMENT.oxblood,
        overflow: 'hidden',
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          onError={() => setFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(201,169,97,0.5)',
            fontSize: 10,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            px: 2,
            textAlign: 'center',
          }}
        >
          {alt}
        </Box>
      )}
    </Box>
  );
};

const PublicPackagePage = ({
  pkg,
  t,
  partnerPreviewMode,
  isPartnerDraftPreview,
  onClientCheckout,
  onCopyClientLink,
  copyLinkBusy = false,
  onEdit,
  clientLinkBusy,
  clientLinkError,
  clientLinkNotice,
  selectedPricing,
  setSelectedPricing,
  inquiryTravelers,
  setInquiryTravelers,
  bookingLoading,
  onBook,
  onAsk,
  onAskAdvisor,
  onExternalBook,
  mapContainerRef,
  mapLoaded,
  mapHasItems,
  selectedAddOnQuantities = {},
  setSelectedAddOnQuantities,
}) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const coverSrc = getPackageCoverSrc(pkg);
  const gallery = getGalleryUrls(pkg.gallery);
  const items = Array.isArray(pkg.items) ? pkg.items : [];
  const itemsByDay = items.reduce((acc, item) => {
    const day = item.day_number || 1;
    if (!acc[day]) acc[day] = [];
    acc[day].push(item);
    return acc;
  }, {});
  const dayEntries = Object.entries(itemsByDay);
  const highlights = items.filter((item) => item.name || item.title).slice(0, 3);
  const included = splitList(selectedPricing?.includes || pkg.price_includes);
  const excluded = splitList(selectedPricing?.excludes || pkg.price_excludes);
  const unitPrice = Number(selectedPricing?.price_per_person || selectedPricing?.price || pkg.base_price || 0);
  const travelers = Math.max(pkg.min_travelers || 1, parseInt(inquiryTravelers, 10) || 1);
  const maxTravelers = pkg.max_travelers || 20;
  const addOns = packageAddOnItems(items).filter((item) => addOnAmount(item) > 0);
  const addOnTotal = sumSelectedAddOns(addOns, selectedAddOnQuantities);
  const total = unitPrice * travelers + addOnTotal;

  const setAddOnQuantity = (itemId, nextQuantity) => {
    if (!setSelectedAddOnQuantities) return;
    const id = Number(itemId);
    const quantity = clampAddOnQuantity(nextQuantity);
    setSelectedAddOnQuantities((current) => {
      const next = { ...(current || {}) };
      if (quantity <= 0) delete next[id];
      else next[id] = quantity;
      return next;
    });
  };
  const partnerName = pkg.partner_name || '';
  const initials = getDocumentInitials(partnerName);
  const expired = Boolean(pkg.is_expired) || isPackageExpired(pkg);
  const primaryLabel = expired
    ? t('publicPackage.expired')
    : pkg.booking_method === 'checkout'
    ? t('publicPackage.bookTour')
    : pkg.booking_method === 'external'
      ? t('publicPackage.bookPartner')
      : pkg.booking_method === 'inquiry'
        ? t('publicPackage.requestBook')
        : t('publicPackage.contactAgent');
  const handlePrimary = () => {
    if (expired) return;
    if (pkg.booking_method === 'external' && pkg.external_booking_url) {
      onExternalBook();
      return;
    }
    onBook();
  };
  const brand = pkg?.branding;
  const partnerLed = Boolean(brand?.level && brand.level !== 'house');
  const headerBg = headerBgFromClientBranding(brand) || DOCUMENT.oxblood;
  const headerLogo = partnerLed && brand.logoUrl ? brand.logoUrl : '/noBgWhite.png';
  const headerName = partnerLed && brand.name ? brand.name : 'Déjà Vu Nomad';
  // House branding carries the Déjà Vu Nomad logo; never show it as the partner's.
  const partnerLogo = (partnerLed && brand.logoUrl) || pkg.partner_logo || null;
  const nights = pkg.duration_nights ?? Math.max(0, Number(pkg.duration_days || 1) - 1);
  const leadParagraphs = [pkg.description, pkg.short_description]
    .filter(Boolean)
    .filter((text, index, list) => list.indexOf(text) === index);

  return (
    <Box
      data-testid="public-package-page"
      data-theme="document"
      sx={{ minHeight: '100vh', bgcolor: DOCUMENT.canvas, color: DOCUMENT.ink, fontFamily: DOCUMENT.sans }}
    >
      <Box
        data-testid="package-page-brand-bar"
        sx={{ bgcolor: headerBg, py: 1, minHeight: { xs: 80, md: 96 }, display: 'flex', alignItems: 'center' }}
      >
        <Box sx={{ ...wrapSx, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {logoFailed ? (
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 21, letterSpacing: '0.13em', color: DOCUMENT.ivory }}>
              {headerName}
            </Typography>
          ) : (
            <Box
              component="img"
              src={headerLogo}
              alt={headerName}
              data-testid="package-page-logo"
              onError={() => setLogoFailed(true)}
              sx={partnerLed ? {
                height: { xs: 40, md: 52 },
                width: 'auto',
                maxWidth: { xs: 220, md: 280 },
                objectFit: 'contain',
                objectPosition: 'left center',
                display: 'block',
              } : documentNavLogoSx}
            />
          )}
        </Box>
      </Box>

      {(partnerPreviewMode || isPartnerDraftPreview) && (
        <Box
          data-testid="package-preview-banner"
          sx={{ bgcolor: '#F2ECE1', borderBottom: `1px solid ${DOCUMENT.ruleHard}`, py: 1.1 }}
        >
          <Box
            sx={{
              ...wrapSx,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              fontSize: 12.5,
              color: DOCUMENT.ink2,
            }}
          >
            <Box>
              {isPartnerDraftPreview ? t('publicPackage.previewDraft') : t('publicPackage.previewPartner')}
            </Box>
            {onEdit ? (
              <Button
                type="button"
                data-testid="package-preview-edit"
                onClick={onEdit}
                sx={{
                  background: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  border: 0,
                  borderRadius: 0,
                  font: 'inherit',
                  fontSize: 11,
                  letterSpacing: '0.13em',
                  textTransform: 'uppercase',
                  px: 2,
                  py: 1,
                  minWidth: 0,
                  boxShadow: 'none',
                  '&:hover': {
                    background: DOCUMENT.nav,
                    boxShadow: 'none',
                  },
                }}
              >
                {t('publicPackage.edit')}
              </Button>
            ) : null}
          </Box>
        </Box>
      )}

      <Box sx={{ position: 'relative', aspectRatio: { xs: '4 / 3', md: '21 / 9' }, bgcolor: DOCUMENT.oxblood, overflow: 'hidden' }}>
        <Box
          component="img"
          src={coverSrc}
          alt=""
          data-testid="package-hero-image"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = getPackageCoverSrc({ destination: pkg.destination, id: pkg.id });
          }}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(20,6,9,0.78) 0%, rgba(20,6,9,0.15) 45%, transparent 70%)',
          }}
        />
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 2, pb: 3.75 }}>
          <Box sx={wrapSx}>
            <Typography
              sx={{
                ...documentEyebrowSx,
                color: DOCUMENT.navAccent,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {pkg.destination && (
                <MapLink
                  location={pkg.destination}
                  fontSize={10}
                  iconSize={12}
                  sx={{
                    color: DOCUMENT.navAccent,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    '& .map-link-icon': { color: DOCUMENT.navAccent },
                    '&:hover': { color: DOCUMENT.navAccent, textDecoration: 'underline' },
                    '&:hover .map-link-icon': { color: DOCUMENT.navAccent },
                  }}
                />
              )}
              {pkg.destination && pkg.duration_days ? '·' : null}
              {pkg.duration_days
                ? `${pkg.duration_days} ${t('publicPackage.days').toLowerCase()}`
                : null}
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: DOCUMENT.display,
                fontWeight: 400,
                fontSize: { xs: 32, md: 58 },
                lineHeight: 1.04,
                color: '#F7F1E8',
                mt: 1.25,
                maxWidth: '17ch',
              }}
            >
              {pkg.title}
            </Typography>
            <Typography sx={{ fontSize: 15, color: '#DCC9BC', mt: 1.5 }}>
              {[
                partnerName ? t('publicPackage.operatedBy', { name: partnerName }) : null,
                formatRange(pkg.available_from, pkg.available_until, ''),
              ].filter(Boolean).join(' · ')}
            </Typography>
            {expired ? (
              <Typography
                sx={{
                  mt: 1.5,
                  display: 'inline-block',
                  fontSize: 11,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: DOCUMENT.nav,
                  bgcolor: DOCUMENT.navAccent,
                  px: 1.25,
                  py: 0.5,
                }}
              >
                {t('publicPackage.expired')}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box sx={wrapSx}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, 1fr)' },
            borderTop: `1px solid ${DOCUMENT.ruleHard}`,
            borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
          }}
        >
          {[
            { label: t('publicPackage.duration'), value: t('publicPackage.durationValue', { days: pkg.duration_days || 0, nights }) },
            { label: t('publicPackage.starts'), location: pkg.start_location || pkg.destination },
            { label: t('publicPackage.ends'), location: pkg.end_location || pkg.start_location || pkg.destination },
            {
              label: t('publicPackage.includes'),
              value: pkg.pricing_type === 'all_inclusive'
                ? t('publicPackage.allInclusive')
                : pkg.pricing_type === 'flight_land'
                  ? t('publicPackage.flightLand')
                  : t('publicPackage.landOnly'),
            },
            { label: t('publicPackage.group'), value: t('publicPackage.upTo', { count: maxTravelers }) },
            { label: t('publicPackage.departures'), value: formatRange(pkg.available_from, pkg.available_until, t('publicPackage.ongoing')) },
          ].map((fact) => (
            <Box
              key={fact.label}
              sx={{
                px: 2.25,
                py: 1.75,
                borderRight: `1px solid ${DOCUMENT.rule}`,
                '&:nth-of-type(2n)': { borderRight: { xs: 0, md: `1px solid ${DOCUMENT.rule}` } },
                '&:last-of-type': { borderRight: 0 },
              }}
            >
              <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.ink3, mb: 0.75 }}>{fact.label}</Typography>
              {fact.location ? (
                <MapLink location={fact.location} fontSize={14.5} iconSize={15} />
              ) : (
                <Typography sx={{ fontSize: 14.5, fontVariantNumeric: 'tabular-nums' }}>{fact.value}</Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        data-testid="package-sticky-book-bar"
        sx={{
          position: 'sticky',
          top: PACKAGE_BOOK_BAR_TOP,
          zIndex: 30,
          bgcolor: DOCUMENT.oxblood,
          color: DOCUMENT.ivory,
          borderBottom: '1px solid rgba(201,169,97,0.3)',
        }}
      >
        <Box sx={{ ...wrapSx, display: 'flex', alignItems: 'center', gap: 2.5, py: 1.5, flexWrap: 'wrap' }}>
          {partnerName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              {partnerLogo ? (
                <PartnerLockup
                  src={partnerLogo}
                  alt={partnerName}
                  banner={headerBg}
                  height={32}
                  maxWidth={220}
                  sx={{ px: 1.25, py: 0.5 }}
                />
              ) : (
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: DOCUMENT.nav,
                    border: `1px solid ${DOCUMENT.navAccent}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    color: DOCUMENT.navAccent,
                  }}
                >
                  {initials || 'P'}
                </Box>
              )}
              <Box>
                <Typography sx={{ fontSize: 14, lineHeight: 1.2 }}>{partnerName}</Typography>
                <Typography sx={{ fontSize: 11, color: DOCUMENT.navAccent, letterSpacing: '0.07em', mt: 0.25 }}>
                  {t('publicPackage.verifiedPartner')}
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ borderLeft: partnerName ? '1px solid rgba(201,169,97,0.3)' : 0, pl: partnerName ? 2.5 : 0 }}>
            <Typography component="span" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, fontWeight: 400 }}>
              {formatMoney(unitPrice)}
            </Typography>
            <Typography component="span" sx={{ fontSize: 12, color: '#DCC9BC', ml: 0.75 }}>
              {t('publicPackage.perPersonFrom')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ml: { xs: 0, md: 'auto' }, flexWrap: 'wrap' }}>
            <Button onClick={handlePrimary} disabled={bookingLoading || expired} sx={goldCtaSx}>
              {bookingLoading ? <CircularProgress size={16} color="inherit" /> : primaryLabel}
            </Button>
            {onClientCheckout ? (
              <Button
                type="button"
                data-testid="package-client-checkout-bar"
                onClick={onClientCheckout}
                disabled={clientLinkBusy}
                sx={goldOutlineCtaSx}
              >
                {clientLinkBusy ? t('publicPackage.clientLinkBusy') : t('publicPackage.clientLink')}
              </Button>
            ) : null}
            {onCopyClientLink ? (
              <Button
                type="button"
                data-testid="package-copy-client-link-bar"
                onClick={onCopyClientLink}
                disabled={copyLinkBusy}
                startIcon={<LinkIcon />}
                sx={goldOutlineCtaSx}
              >
                {copyLinkBusy ? t('publicPackage.clientLinkBusy') : t('publicPackage.copyClientLink')}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>

      {expired ? (
        <Box sx={{ ...wrapSx, mt: 2 }}>
          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}`, px: 2, py: 1.5 }}>
            {t('publicPackage.expiredHelp')}
          </Typography>
        </Box>
      ) : null}

      <Box sx={wrapSx}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 340px' },
            gap: { xs: 0, md: 7 },
            mt: partnerPreviewMode ? 0 : 5.5,
            pb: 10,
            alignItems: 'start',
          }}
        >
          <Box component="main">
            <Box component="section" sx={{ mb: 5.75 }}>
              <Typography component="h2" sx={sectionTitleSx}>{t('publicPackage.theTrip')}</Typography>
              {leadParagraphs.map((text) => (
                <Typography
                  key={text.slice(0, 24)}
                  sx={{ fontSize: 16, lineHeight: 1.75, color: DOCUMENT.ink2, maxWidth: '64ch', mb: 1.75, whiteSpace: 'pre-line' }}
                >
                  {text}
                </Typography>
              ))}
              {highlights.length > 0 && (
                <Box sx={{ mt: 2.75, borderTop: `1px solid ${DOCUMENT.rule}` }}>
                  {highlights.map((item, index) => (
                    <Box
                      key={`${item.name || item.title}-${index}`}
                      sx={{ display: 'flex', gap: 2, py: 1.6, borderBottom: `1px solid ${DOCUMENT.rule}` }}
                    >
                      <Typography sx={{ fontSize: 11, letterSpacing: '0.16em', color: DOCUMENT.navAccent, pt: 0.4, flexShrink: 0 }}>
                        {String(index + 1).padStart(2, '0')}
                      </Typography>
                      <Box>
                        <Typography sx={{ fontSize: 15, mb: 0.5 }}>{item.name || item.title}</Typography>
                        {item.description && (
                          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, lineHeight: 1.6 }}>
                            {item.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {mapHasItems && (
              <Box component="section" sx={{ mb: 5.75 }}>
                <Typography component="h2" sx={sectionTitleSx}>{t('publicPackage.routeMap')}</Typography>
                <Box sx={{ position: 'relative' }}>
                  <Box ref={mapContainerRef} sx={{ ...documentMapBoxSx, height: 360, minHeight: 360 }} />
                  {!mapLoaded && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(252,251,248,0.9)',
                      }}
                    >
                      <Typography sx={{ color: DOCUMENT.ink3 }}>{t('publicPackage.loadingMap')}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Box component="section" sx={{ mb: 5.75 }}>
              <Typography component="h2" sx={sectionTitleSx}>
                {t('publicPackage.dayByDay')}
                {items.length > 0 && (
                  <Typography component="span" sx={{ fontFamily: DOCUMENT.sans, fontSize: 12.5, color: DOCUMENT.ink3, ml: 1.5 }}>
                    {t('publicPackage.activitiesCount', { days: pkg.duration_days || dayEntries.length, count: items.length })}
                  </Typography>
                )}
              </Typography>

              {dayEntries.length === 0 ? (
                <Typography sx={{ color: DOCUMENT.ink2 }}>{t('publicPackage.itineraryPending')}</Typography>
              ) : dayEntries.map(([day, dayItems]) => {
                const heading = dayItems.find((item) => item.location)?.location || t('publicPackage.dayActivities', { n: day });
                return (
                  <Box key={day} sx={{ mb: 3.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, pb: 1.4, borderBottom: `1px solid ${DOCUMENT.ruleHard}` }}>
                      <Typography sx={{ ...documentEyebrowSx, width: 48, flexShrink: 0 }}>
                        {t('publicPackage.dayN', { n: String(day).padStart(2, '0') })}
                      </Typography>
                      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 23, fontWeight: 400, lineHeight: 1 }}>
                        {heading}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        position: 'relative',
                        pl: { xs: 0, md: '106px' },
                        '&::before': {
                          display: { xs: 'none', md: 'block' },
                          content: '""',
                          position: 'absolute',
                          left: 87,
                          top: 0,
                          bottom: 18,
                          width: '1px',
                          bgcolor: DOCUMENT.ruleHard,
                        },
                      }}
                    >
                      {dayItems.map((item, index) => (
                        <Box
                          key={`${item.id || item.name}-${index}`}
                          sx={{
                            position: 'relative',
                            py: 2.75,
                            borderTop: index > 0 ? `1px solid ${DOCUMENT.rule}` : 0,
                            '&::after': {
                              display: { xs: 'none', md: 'block' },
                              content: '""',
                              position: 'absolute',
                              left: -20,
                              top: 30,
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              bgcolor: DOCUMENT.canvas,
                              border: `1px solid ${DOCUMENT.navAccent}`,
                            },
                          }}
                        >
                          {(item.start_datetime || item.end_datetime) && (
                            <Box
                              sx={{
                                position: { xs: 'static', md: 'absolute' },
                                left: { md: -106 },
                                top: { md: 24 },
                                width: { md: 70 },
                                textAlign: { md: 'right' },
                                mb: { xs: 1, md: 0 },
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              <Typography sx={{ fontSize: 15 }}>{formatClock(item.start_datetime)}</Typography>
                              {item.end_datetime && (
                                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.25 }}>
                                  {formatClock(item.end_datetime)}
                                </Typography>
                              )}
                            </Box>
                          )}
                          <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.navAccent }}>
                            {t(`publicPackage.${categoryKey(item.category)}`)}
                          </Typography>
                          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 21, fontWeight: 400, lineHeight: 1.16, my: 0.75 }}>
                            {item.name || item.title}
                          </Typography>
                          {!isIncludedPackageItem(item) && (
                            <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.accent, mt: 0.5 }}>
                              {t('publicPackage.addOnPrice', {
                                amount: `${item.currency || item.cost_currency || pkg.currency || 'USD'} ${Number(item.additional_cost || item.cost || 0).toFixed(0)}`
                              })}
                            </Typography>
                          )}
                          {item.location && (
                            <MapLink item={item} fontSize={13.5} />
                          )}
                          {item.description && (
                            <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mt: 1.25, lineHeight: 1.7, maxWidth: '60ch' }}>
                              {item.description}
                            </Typography>
                          )}
                          {item.image_url && (
                            <Box data-testid="itinerary-item-media" sx={{ mt: 1.75, width: '100%' }}>
                              <PackagePhoto src={item.image_url} alt={item.name || item.title} ratio="3 / 2" />
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {(included.length > 0 || excluded.length > 0) && (
              <Box component="section" sx={{ mb: 5.75 }}>
                <Typography component="h2" sx={sectionTitleSx}>{t('publicPackage.priceCovers')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 3, sm: '0 40px' } }}>
                  {included.length > 0 && (
                    <Box>
                      <Typography sx={documentEyebrowSx}>{t('publicPackage.included')}</Typography>
                      <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 1.4 }}>
                        {included.map((item) => (
                          <Box
                            component="li"
                            key={item}
                            sx={{ py: 1.25, borderBottom: `1px solid ${DOCUMENT.rule}`, display: 'flex', gap: 1.5, fontSize: 14.5 }}
                          >
                            <Box sx={{ color: DOCUMENT.navAccent, fontSize: 11, pt: 0.5 }}>✓</Box>
                            <Box>{item}</Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {excluded.length > 0 && (
                    <Box>
                      <Typography sx={documentEyebrowSx}>{t('publicPackage.notIncluded')}</Typography>
                      <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 1.4 }}>
                        {excluded.map((item) => (
                          <Box
                            component="li"
                            key={item}
                            sx={{ py: 1.25, borderBottom: `1px solid ${DOCUMENT.rule}`, display: 'flex', gap: 1.5, fontSize: 14.5, color: DOCUMENT.ink2 }}
                          >
                            <Box sx={{ color: DOCUMENT.ink3, fontSize: 11, pt: 0.5 }}>—</Box>
                            <Box>{item}</Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {gallery.length > 0 && (
              <Box component="section" sx={{ mb: 5.75 }}>
                <Typography component="h2" sx={sectionTitleSx}>{t('publicPackage.photographs')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                  {gallery.map((url, index) => (
                    <Box key={url} component="figure" sx={{ m: 0 }}>
                      <PackagePhoto src={url} alt={`${pkg.title} ${index + 1}`} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {(pkg.booking_deadline_days || pkg.customization_notes || pkg.max_travelers || pkg.cancellation_policy) && (
              <Box component="section" sx={{ mb: 5.75 }}>
                <Typography component="h2" sx={sectionTitleSx}>{t('publicPackage.practical')}</Typography>
                <Box>
                  {pkg.booking_deadline_days ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: { xs: 0.5, sm: 3 }, py: 1.75, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                      <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.ink3, pt: 0.4 }}>{t('publicPackage.bookingCloses')}</Typography>
                      <Typography sx={{ fontSize: 14.5, lineHeight: 1.65, color: DOCUMENT.ink2 }}>
                        {t('publicPackage.bookingClosesDays', { count: pkg.booking_deadline_days })}
                      </Typography>
                    </Box>
                  ) : null}
                  {pkg.cancellation_policy ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: { xs: 0.5, sm: 3 }, py: 1.75, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                      <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.ink3, pt: 0.4 }}>{t('publicPackage.cancellation')}</Typography>
                      <Typography sx={{ fontSize: 14.5, lineHeight: 1.65, color: DOCUMENT.ink2 }}>
                        {t(`publicPackage.policies.${pkg.cancellation_policy}`, {
                          defaultValue: t('publicPackage.policies.moderate'),
                        })}
                      </Typography>
                    </Box>
                  ) : null}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: { xs: 0.5, sm: 3 }, py: 1.75, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                    <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.ink3, pt: 0.4 }}>{t('publicPackage.group')}</Typography>
                    <Typography sx={{ fontSize: 14.5, lineHeight: 1.65, color: DOCUMENT.ink2 }}>
                      {t('publicPackage.upTo', { count: maxTravelers })}
                    </Typography>
                  </Box>
                  {pkg.customization_notes ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: { xs: 0.5, sm: 3 }, py: 1.75, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                      <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.ink3, pt: 0.4 }}>{t('publicPackage.notes')}</Typography>
                      <Typography sx={{ fontSize: 14.5, lineHeight: 1.65, color: DOCUMENT.ink2 }}>{pkg.customization_notes}</Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            )}

            {partnerName && (
              <Box component="section">
                <Typography component="h2" sx={sectionTitleSx}>{t('publicPackage.whoRuns')}</Typography>
                <Box
                  sx={{
                    border: `1px solid ${DOCUMENT.ruleHard}`,
                    bgcolor: DOCUMENT.surface,
                    p: 3,
                    display: 'flex',
                    gap: 2.5,
                    alignItems: 'flex-start',
                  }}
                >
                  {partnerLogo ? (
                    <PartnerLockup
                      src={partnerLogo}
                      alt={partnerName}
                      banner={headerBg}
                      height={44}
                      maxWidth={240}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: DOCUMENT.oxblood,
                        color: DOCUMENT.navAccent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: DOCUMENT.display,
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      {initials || 'P'}
                    </Box>
                  )}
                  <Box>
                    <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 21, fontWeight: 400, mb: 0.5 }}>
                      {partnerName}
                    </Typography>
                    <Typography sx={{ ...documentEyebrowSx, mb: 1.4 }}>{t('publicPackage.verifiedPartner')}</Typography>
                    {pkg.partner_website && (
                      <Typography
                        component="a"
                        href={pkg.partner_website}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: 13.5, color: DOCUMENT.ink2, display: 'block' }}
                      >
                        {pkg.partner_website}
                      </Typography>
                    )}
                    {onAskAdvisor ? (
                      <Button
                        onClick={onAskAdvisor}
                        data-testid="ask-this-advisor"
                        sx={{
                          mt: 1.5,
                          px: 0,
                          minWidth: 0,
                          background: 'none',
                          color: DOCUMENT.oxblood,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          fontSize: 11,
                          justifyContent: 'flex-start',
                        }}
                      >
                        {t('publicPackage.askThisAdvisor')}
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Box
            component="aside"
            data-testid="package-booking-aside"
            sx={{ order: { xs: -1, md: 0 }, mb: { xs: 5, md: 0 } }}
          >
            <Box
              sx={{
                position: { md: 'sticky' },
                top: PACKAGE_ASIDE_TOP,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                bgcolor: DOCUMENT.surface,
              }}
            >
              <Box sx={{ px: 2.5, pt: 2, pb: 1.75, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>{t('publicPackage.from')}</Typography>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 32, fontWeight: 400, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(unitPrice)}
                  <Typography component="em" sx={{ fontStyle: 'normal', fontFamily: DOCUMENT.sans, fontSize: 13, color: DOCUMENT.ink3, ml: 0.75 }}>
                    {t('publicPackage.perPerson').replace(/^\s*\/\s*/, '')}
                  </Typography>
                </Typography>
              </Box>

              <Box sx={{ px: 2.5, py: 2.25 }}>
                {pkg.pricing?.length > 0 && (
                  <>
                    <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.ink3, mb: 1.1 }}>{t('publicPackage.chooseRate')}</Typography>
                    <Box sx={{ display: 'grid', gap: 1, mb: 2.5 }}>
                      {pkg.pricing.map((option) => {
                        const selected = selectedPricing?.id === option.id;
                        return (
                          <Box
                            key={option.id}
                            component="button"
                            type="button"
                            onClick={() => setSelectedPricing(option)}
                            aria-pressed={selected}
                            sx={{
                              background: selected ? '#F6F1E7' : 'none',
                              border: `1px solid ${selected ? DOCUMENT.navAccent : DOCUMENT.ruleHard}`,
                              textAlign: 'left',
                              font: 'inherit',
                              p: 1.5,
                              cursor: 'pointer',
                              color: 'inherit',
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 1.5,
                            }}
                          >
                            <Box>
                              <Typography sx={{ fontSize: 14.5 }}>{option.tier_name || option.name}</Typography>
                              {(option.occupancy_type || option.room_type || option.description) && (
                                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.4 }}>
                                  {option.occupancy_type || option.room_type || option.description}
                                </Typography>
                              )}
                            </Box>
                            <Typography sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums', fontSize: 15, whiteSpace: 'nowrap' }}>
                              {formatMoney(option.price_per_person || option.price)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}

                <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.ink3, mb: 1.1 }}>{t('publicPackage.travelers')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${DOCUMENT.ruleHard}`, width: 'fit-content', mb: 2.5 }}>
                  <Button
                    onClick={() => setInquiryTravelers(Math.max(pkg.min_travelers || 1, travelers - 1))}
                    sx={{ minWidth: 0, px: 1.8, py: 0.75, color: DOCUMENT.ink2, borderRadius: 0 }}
                  >
                    −
                  </Button>
                  <Box sx={{ px: 2, borderLeft: `1px solid ${DOCUMENT.ruleHard}`, borderRight: `1px solid ${DOCUMENT.ruleHard}`, lineHeight: '34px', fontVariantNumeric: 'tabular-nums' }}>
                    {travelers}
                  </Box>
                  <Button
                    onClick={() => setInquiryTravelers(Math.min(maxTravelers, travelers + 1))}
                    sx={{ minWidth: 0, px: 1.8, py: 0.75, color: DOCUMENT.ink2, borderRadius: 0 }}
                  >
                    +
                  </Button>
                </Box>

                {addOns.length > 0 && (
                  <Box sx={{ mb: 2.5 }} data-testid="package-page-addons">
                    <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.ink3, mb: 1.1 }}>
                      {t('publicPackage.addOns')}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mb: 1.25, lineHeight: 1.5 }}>
                      {t('publicPackage.addOnsHint')}
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      {addOns.map((item) => {
                        const quantity = clampAddOnQuantity(
                          selectedAddOnQuantities?.[item.id] ?? selectedAddOnQuantities?.[String(item.id)]
                        );
                        const selected = quantity > 0;
                        const amount = addOnAmount(item);
                        const lineTotal = amount * Math.max(quantity, 1);
                        return (
                          <Box
                            key={item.id}
                            data-testid={`package-page-addon-${item.id}`}
                            aria-pressed={selected}
                            sx={{
                              background: selected ? '#F6F1E7' : 'none',
                              border: `1px solid ${selected ? DOCUMENT.navAccent : DOCUMENT.ruleHard}`,
                              textAlign: 'left',
                              p: 1.5,
                              color: 'inherit',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14.5 }}>{item.name || item.title}</Typography>
                                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.4 }}>
                                  {item.day_number
                                    ? t('publicPackage.addOnDay', { n: String(item.day_number).padStart(2, '0') })
                                    : t('publicPackage.addOn')}
                                  {selected
                                    ? ` · ${t('publicPackage.addOnUnitPrice', { price: formatMoney(amount) })}`
                                    : ''}
                                </Typography>
                              </Box>
                              <Typography sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums', fontSize: 15, whiteSpace: 'nowrap' }}>
                                {formatMoney(selected ? lineTotal : amount)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                border: `1px solid ${DOCUMENT.ruleHard}`,
                                bgcolor: DOCUMENT.paper,
                              }}
                            >
                              <Button
                                onClick={() => setAddOnQuantity(item.id, quantity - 1)}
                                disabled={quantity <= 0}
                                aria-label={t('publicPackage.addOnDecrease')}
                                data-testid={`package-page-addon-dec-${item.id}`}
                                sx={{ minWidth: 0, px: 1.5, py: 0.5, color: DOCUMENT.ink2, borderRadius: 0 }}
                              >
                                −
                              </Button>
                              <Box
                                sx={{
                                  px: 1.75,
                                  borderLeft: `1px solid ${DOCUMENT.ruleHard}`,
                                  borderRight: `1px solid ${DOCUMENT.ruleHard}`,
                                  lineHeight: '30px',
                                  fontVariantNumeric: 'tabular-nums',
                                  minWidth: 36,
                                  textAlign: 'center',
                                }}
                                data-testid={`package-page-addon-qty-${item.id}`}
                              >
                                {quantity}
                              </Box>
                              <Button
                                onClick={() => setAddOnQuantity(item.id, quantity + 1)}
                                disabled={quantity >= MAX_PACKAGE_ADDON_QUANTITY}
                                aria-label={t('publicPackage.addOnIncrease')}
                                data-testid={`package-page-addon-inc-${item.id}`}
                                sx={{ minWidth: 0, px: 1.5, py: 0.5, color: DOCUMENT.ink2, borderRadius: 0 }}
                              >
                                +
                              </Button>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                <Box sx={{ borderTop: `1px solid ${DOCUMENT.rule}`, pt: 1.75 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6, fontSize: 13.5, color: DOCUMENT.ink2 }}>
                    <span>{t('publicPackage.travellersLine', { price: formatMoney(unitPrice), count: travelers })}</span>
                    <span>{formatMoney(unitPrice * travelers)}</span>
                  </Box>
                  {addOnTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6, fontSize: 13.5, color: DOCUMENT.ink2 }}>
                      <span>{t('publicPackage.addOns')}</span>
                      <span>{formatMoney(addOnTotal)}</span>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6, fontSize: 13.5, color: DOCUMENT.ink2 }}>
                    <span>{t('publicPackage.taxesIncluded')}</span>
                    <span>{t('publicPackage.included')}</span>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: `1px solid ${DOCUMENT.rule}`, mt: 1.1, pt: 1.5 }}>
                    <span>{t('publicPackage.total')}</span>
                    <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, fontWeight: 400 }}>{formatMoney(total)}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ px: 2.5, pb: 2.5 }}>
                <Button onClick={handlePrimary} disabled={bookingLoading || expired} sx={oxbloodCtaSx}>
                  {bookingLoading ? <CircularProgress size={18} color="inherit" /> : primaryLabel}
                </Button>
                {onClientCheckout ? (
                  <Button
                    type="button"
                    data-testid="package-client-checkout"
                    onClick={onClientCheckout}
                    disabled={clientLinkBusy}
                    sx={sidebarClientCtaSx}
                  >
                    {clientLinkBusy ? t('publicPackage.clientLinkBusy') : t('publicPackage.clientLink')}
                  </Button>
                ) : null}
                {onCopyClientLink ? (
                  <Button
                    type="button"
                    data-testid="package-copy-client-link"
                    onClick={onCopyClientLink}
                    disabled={copyLinkBusy}
                    startIcon={<LinkIcon />}
                    sx={sidebarClientCtaSx}
                  >
                    {copyLinkBusy ? t('publicPackage.clientLinkBusy') : t('publicPackage.copyClientLink')}
                  </Button>
                ) : null}
                {clientLinkError ? (
                  <Typography sx={{ fontSize: 12, color: '#8C2B22', mt: 1.1, textAlign: 'center' }}>
                    {clientLinkError}
                  </Typography>
                ) : null}
                {clientLinkNotice ? (
                  <Typography sx={{ fontSize: 12, color: DOCUMENT.oxblood, mt: 1.1, textAlign: 'center' }}>
                    {clientLinkNotice}
                  </Typography>
                ) : null}
                <Button
                  onClick={onAsk}
                  disabled={expired}
                  sx={{
                    width: '100%',
                    background: 'none',
                    color: DOCUMENT.ink2,
                    letterSpacing: '0.11em',
                    textTransform: 'uppercase',
                    fontSize: 11,
                    pt: 1.5,
                    '&.Mui-disabled': { color: DOCUMENT.ink3 },
                  }}
                >
                  {t('publicPackage.askFirst')}
                </Button>
                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, lineHeight: 1.6, mt: 1.6, textAlign: 'center' }}>
                  {expired
                    ? t('publicPackage.expiredHelp')
                    : t('publicPackage.reassure', { name: partnerName || t('publicPackage.contactAgent') })}
                </Typography>
                <SellerDisclosureBox seller={sellerFromPackage(pkg)} sx={{ mt: 2, textAlign: 'left' }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PublicPackagePage;
